import { postInputEndpoint } from "@/lib/input-api"

/** One entry of `POST /input/list_playlists`, as `Youtube::getPlaylists` builds it. */
export type InputPlaylist = {
  id: string
  title: string
  description?: string
}

type Constants = Record<string, number>

/**
 * An input name with its playlist suffix swapped: picking a playlist appends
 * " - <title>" so one channel's sources can be told apart. `from` is the title
 * being replaced and `to` the one being added; either may be absent. Only an
 * exact trailing " - <from>" is removed, so a name the reader edited, or a
 * channel name with " - " in it, is left alone.
 */
export function playlistName(
  name: string,
  from?: string | null,
  to?: string | null
): string {
  const suffix = from ? ` - ${from}` : ""
  const base = suffix && name.endsWith(suffix) ? name.slice(0, -suffix.length) : name

  return to ? `${base} - ${to}` : base
}

/**
 * The `POST /input/list_playlists` body for a YouTube input being created, or
 * null when it carries nothing the endpoint can resolve creds from.
 *
 * The handles, and their order, are the ones the save builds the input from
 * (`Input::setupSocialCred`): the OAuth return's `credID`, then a reused
 * SocialCred's `scID`. A reconnected reused account comes back with a
 * REAUTHREUSE `credID`, whose session holds the fresh tokens; its old `scID`
 * still holds the expired ones. The `inputId` an OAuth return also carries is
 * deliberately unused: that incomplete input backs the preview, not the save.
 */
export function inputPlaylistsRequest(
  input: Record<string, any> | null | undefined,
  constants: Constants
): Record<string, string | number> | null {
  if (!input || parseInt(String(input.type ?? ""), 10) !== constants.INPUT_TYPE_YOUTUBE) {
    return null
  }

  const type = constants.INPUT_TYPE_YOUTUBE
  if (input.credID) return { type, credID: String(input.credID) }
  if (input.scID) return { type, scID: parseInt(String(input.scID), 10) }

  return null
}

/**
 * The channel's playlists, or null when there is nothing to fetch them with or
 * the request failed. An empty list is drawn differently from null.
 */
export async function fetchInputPlaylists(
  apiUrl: string,
  input: Record<string, any> | null | undefined,
  constants: Constants
): Promise<InputPlaylist[] | null> {
  const request = inputPlaylistsRequest(input, constants)
  if (!request) return null

  try {
    const data = await postInputEndpoint(apiUrl, "list_playlists", request)

    return Array.isArray(data) ? (data as InputPlaylist[]) : null
  } catch {
    return null
  }
}
